import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import HeaderBar from "@/auth/HeaderBar";
import Footer from "@/components/landing/Footer";
import { useCart } from "@/cart/CartProvider";
import { getSortedPosts } from "@/data/blogPosts";

export default function Blogs() {
    const { openAuth } = useCart();
    const posts = getSortedPosts();

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "Blog · Caribbean Meal Prep";
    }, []);

    return (
        <div className="min-h-screen bg-cream text-navy font-sans flex flex-col">
            <div className="relative hero-gradient text-white pb-20 pt-32 px-6">
                <HeaderBar onSignIn={openAuth} dark />
                <div className="max-w-5xl mx-auto">
                    <Link
                        to="/"
                        data-testid="blog-back-home"
                        className="inline-flex items-center gap-1.5 text-mint hover:text-gold transition-colors text-sm font-medium"
                    >
                        <ArrowLeft size={16} /> Back to home
                    </Link>
                    <p className="mt-6 text-[11px] tracking-[0.4em] text-gold font-bold uppercase">
                        Caribbean Meal Prep Blog
                    </p>
                    <h1
                        data-testid="blog-index-title"
                        className="mt-3 font-serif font-black text-4xl sm:text-5xl md:text-6xl leading-[1.05]"
                    >
                        Recipes, stories &{" "}
                        <span className="text-gold italic">meal-prep tips</span>
                    </h1>
                    <p className="mt-5 text-white/85 text-base sm:text-lg max-w-2xl">
                        Healthy eating ideas, behind-the-scenes notes from our
                        Boston kitchen, and Caribbean-inspired ways to fuel your
                        week.
                    </p>
                </div>
            </div>

            <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-16">
                {posts.length === 0 ? (
                    <p className="text-center text-muted2">
                        New posts coming soon.
                    </p>
                ) : (
                    <ul
                        data-testid="blog-grid"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-7"
                    >
                        {posts.map((post) => (
                            <li
                                key={post.slug}
                                data-testid={`blog-card-${post.slug}`}
                            >
                                <Link
                                    to={`/blogs/${post.slug}`}
                                    className="group block bg-white rounded-[20px] overflow-hidden border border-mint shadow-[0_8px_24px_rgba(13,40,24,0.06)] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_14px_32px_rgba(13,40,24,0.12)] h-full"
                                >
                                    <div className="aspect-[4/3] overflow-hidden bg-mint/30">
                                        <img
                                            src={post.hero_image}
                                            alt={post.title}
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-5">
                                        {post.category && (
                                            <span className="inline-block px-2.5 py-0.5 rounded-full bg-gold/20 text-burnt text-[10px] font-bold uppercase tracking-widest">
                                                {post.category}
                                            </span>
                                        )}
                                        <h2 className="mt-3 font-serif font-bold text-navy text-lg leading-snug group-hover:text-forest transition-colors line-clamp-3">
                                            {post.title}
                                        </h2>
                                        <p className="mt-2 text-sm text-muted2 leading-relaxed line-clamp-3">
                                            {post.excerpt}
                                        </p>
                                        <div className="mt-4 flex items-center gap-3 text-[11px] text-muted2">
                                            <span className="inline-flex items-center gap-1">
                                                <Calendar size={11} />
                                                {post.date}
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <Clock size={11} />
                                                {post.read_time}
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </main>

            <Footer />
        </div>
    );
}
